import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useGroomingProjects } from "@/hooks/queries/use-grooming";
import { useWorkItemProjects } from "@/hooks/queries/use-work-items";
import { getAdoPatErrorCode } from "@/lib/api/work-items";
import { filterProjects, unconfiguredProjects } from "@/lib/grooming/unconfigured-projects";
import { projectPath } from "@/lib/grooming/paths";
import { adoSetupRedirect } from "@/lib/toolbox/page-names";
import { TOOLBOX_TEXT } from "@/lib/toolbox/type-scale";
import { cn } from "@/lib/utils";

/**
 * The picker behind the Admin-only "Add project…" item: the ADO projects the
 * Admin's own token lists, minus those in the project list. Pick only, never
 * type a project name (03-evaluation.md section 8.6 Q7).
 *
 * The exclusion set is the project list itself (`GET /grooming/projects`,
 * grooming switched on). A project that is set up but switched off is offered
 * on purpose: the switcher does not list it, a new project starts switched
 * off, so this picker is the Admin's way back to it. Picking it opens its
 * saved Repositories tab; nothing is overwritten.
 *
 * Mount it only while an Admin has it open: the ADO read starts on mount, and
 * a remount retries a failed read.
 */

/** The plain sentence for a failed ADO projects read; exported for tests. */
export function addProjectListErrorText(error: unknown): string {
  const code = getAdoPatErrorCode(error);
  if (code === "ADO_PAT_REQUIRED") {
    return "Connect Azure DevOps first so SpecTree can list your ADO projects.";
  }
  if (code === "ADO_PAT_INVALID") {
    return "Azure DevOps did not accept your token, so SpecTree cannot list your ADO projects. It may have expired. Make a new one with Work Items: Read & write and Code: Read ticked.";
  }
  return "Your ADO projects could not be read from Azure DevOps. Try again in a moment.";
}

/** The sentence when the set-up projects cannot be read; exported for tests. */
export const ADD_PROJECT_CONFIGURED_ERROR_TEXT =
  "SpecTree could not read which projects are already set up, so it cannot tell which ones to offer. Try again in a moment.";

export interface AddProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddProjectDialog({ open, onOpenChange }: AddProjectDialogProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const adoProjects = useWorkItemProjects();
  const configured = useGroomingProjects();

  const available = useMemo(
    () => unconfiguredProjects(adoProjects.data ?? [], configured.data ?? []),
    [adoProjects.data, configured.data]
  );
  const shown = useMemo(() => filterProjects(available, query), [available, query]);

  const loading = adoProjects.isLoading || configured.isLoading;
  const error = adoProjects.isError ? adoProjects.error : null;
  const patError = error ? getAdoPatErrorCode(error) !== undefined : false;
  // Without the set-up list every ADO project would look new; offer nothing.
  const configuredError = !error && configured.isError;

  const pick = (name: string) => {
    void navigate(projectPath(name, "repositories"));
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a project</DialogTitle>
          <DialogDescription>
            Choose the ADO project to set up. Next you add the repositories Claude reads for it.
          </DialogDescription>
        </DialogHeader>
        <Input
          aria-label="Search ADO projects"
          autoFocus
          value={query}
          onChange={(event) => { setQuery(event.target.value); }}
          placeholder="Search ADO projects"
          className={TOOLBOX_TEXT.content}
        />
        <div className="max-h-72 overflow-y-auto">
          {loading ? (
            <div className="flex items-center gap-2 py-3" role="status">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span className="sr-only">Loading…</span>
            </div>
          ) : error ? (
            <p
              className={cn(TOOLBOX_TEXT.content, "text-destructive")}
              role="alert"
              data-testid="add-project-error"
            >
              {addProjectListErrorText(error)}
              {patError ? (
                <>
                  {" "}
                  <Link
                    className="text-link underline-offset-2 hover:underline"
                    to={adoSetupRedirect(location.pathname, location.search)}
                    onClick={() => { onOpenChange(false); }}
                  >
                    Open Settings
                  </Link>
                </>
              ) : null}
            </p>
          ) : configuredError ? (
            <p
              className={cn(TOOLBOX_TEXT.content, "text-destructive")}
              role="alert"
              data-testid="add-project-error"
            >
              {ADD_PROJECT_CONFIGURED_ERROR_TEXT}
            </p>
          ) : available.length === 0 ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="add-project-empty">
              Every ADO project your token can see is already set up. Only projects whose ADO process
              SpecTree supports are listed.
            </p>
          ) : shown.length === 0 ? (
            <p className={cn(TOOLBOX_TEXT.content, "text-muted-foreground")} data-testid="add-project-empty">
              No ADO project matches &quot;{query.trim()}&quot;.
            </p>
          ) : (
            <ul aria-label="ADO projects" className="flex flex-col gap-1">
              {shown.map((project) => (
                <li key={project.name}>
                  <button
                    type="button"
                    data-testid="add-project-option"
                    className={cn(
                      TOOLBOX_TEXT.content,
                      "w-full rounded-md px-2.5 py-1.5 text-left text-foreground hover:bg-accent focus-visible:bg-accent focus-visible:outline-none"
                    )}
                    onClick={() => { pick(project.name); }}
                  >
                    {project.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
